import DOMPurify from 'dompurify';
import type {
  ContentBankAccess,
  ContentBankDocument,
  ContentBankMetadata,
  ContentBankVisibility,
  TiptapJsonNode,
} from './types';

export const EMPTY_TIPTAP_DOC: TiptapJsonNode = {
  type: 'doc',
  content: [
    {
      type: 'paragraph',
    },
  ],
};

export const EMPTY_CONTENT_BANK_METADATA: ContentBankMetadata = {
  summary: '',
  categories: [],
  tags: [],
  searchKeywords: [],
  likelyQuestions: [],
};

export const CONTENT_BANK_VISIBILITY_LABELS: Record<ContentBankVisibility, string> = {
  public: 'Public',
  customers: 'Kunder',
  specificCustomers: 'Specifikke kunder',
  internal: 'Intern',
};

export const EMPTY_CONTENT_BANK_ACCESS: ContentBankAccess = {
  visibility: 'public',
  orgIds: [],
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function normalizeStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

export function normalizeContentBankMetadata(value: unknown): ContentBankMetadata {
  if (!isRecord(value)) {
    return EMPTY_CONTENT_BANK_METADATA;
  }

  return {
    summary: typeof value.summary === 'string' ? value.summary.trim() : '',
    categories: normalizeStringList(value.categories),
    tags: normalizeStringList(value.tags),
    searchKeywords: normalizeStringList(value.searchKeywords),
    likelyQuestions: normalizeStringList(value.likelyQuestions),
  };
}

export function normalizeContentBankAccess(value: unknown): ContentBankAccess {
  if (!isRecord(value)) {
    return EMPTY_CONTENT_BANK_ACCESS;
  }

  const visibility =
    value.visibility === 'public' ||
    value.visibility === 'customers' ||
    value.visibility === 'specificCustomers' ||
    value.visibility === 'internal'
      ? value.visibility
      : 'public';

  return {
    visibility,
    orgIds: visibility === 'specificCustomers' ? normalizeStringList(value.orgIds) : [],
  };
}

export function splitStringList(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/\n|,/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

export function joinStringList(value: string[]) {
  return value.join(', ');
}

export function toCallableJsonValue<T>(value: T): T {
  const serialized = JSON.stringify(value);

  return typeof serialized === 'string' ? (JSON.parse(serialized) as T) : value;
}

export function createContentBankTableJson(rows = 3, columns = 3): TiptapJsonNode {
  return {
    type: 'table',
    content: Array.from({ length: rows }, (_, rowIndex) => ({
      type: 'tableRow',
      content: Array.from({ length: columns }, () => ({
        type: rowIndex === 0 ? 'tableHeader' : 'tableCell',
        content: [{ type: 'paragraph' }],
      })),
    })),
  };
}

export function sanitizeContentBankPasteHtml(html: string) {
  return String(
    DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'a',
        'b',
        'blockquote',
        'br',
        'col',
        'colgroup',
        'div',
        'em',
        'h1',
        'h2',
        'h3',
        'i',
        'li',
        'ol',
        'p',
        'span',
        'strong',
        'table',
        'tbody',
        'td',
        'tfoot',
        'th',
        'thead',
        'tr',
        'u',
        'ul',
      ],
      ALLOWED_ATTR: [
        'align',
        'bgcolor',
        'colspan',
        'data-colwidth',
        'href',
        'rel',
        'rowspan',
        'style',
        'target',
      ],
      ALLOW_DATA_ATTR: false,
      FORBID_TAGS: [
        'audio',
        'embed',
        'form',
        'iframe',
        'img',
        'input',
        'link',
        'meta',
        'object',
        'script',
        'source',
        'style',
        'svg',
        'video',
      ],
      KEEP_CONTENT: true,
    })
  );
}

export function extractTextFromTiptapDocument(node: TiptapJsonNode | null | undefined): string {
  const parts: string[] = [];

  function visit(current: TiptapJsonNode | null | undefined) {
    if (!current) {
      return;
    }

    if (current.text) {
      parts.push(current.text);
    }

    if (current.type === 'contentImage') {
      const alt = typeof current.attrs?.alt === 'string' ? current.attrs.alt : '';
      if (alt.trim()) {
        parts.push(alt.trim());
      }
    }

    if (current.type === 'contentVideo') {
      const title = typeof current.attrs?.title === 'string' ? current.attrs.title : '';
      const url = typeof current.attrs?.url === 'string' ? current.attrs.url : '';
      if (title.trim()) {
        parts.push(title.trim());
      } else if (url.trim()) {
        parts.push(url.trim());
      }
    }

    current.content?.forEach(visit);

    if (
      current.type &&
      [
        'paragraph',
        'heading',
        'listItem',
        'blockquote',
        'tableCell',
        'tableHeader',
        'tableRow',
        'contentImage',
        'contentVideo',
      ].includes(current.type)
    ) {
      parts.push('\n');
    }
  }

  visit(node);

  return parts
    .join(' ')
    .replace(/\s*\n\s*/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function slugifyContentBankTitle(title: string, fallbackId = '') {
  const slug = title
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 72);

  if (slug) {
    return slug;
  }

  return fallbackId ? `dokument-${fallbackId.slice(0, 8)}` : 'dokument';
}

function normalizeSearchText(value: string) {
  return value
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenizeSearchText(value: string) {
  return normalizeSearchText(value)
    .split(' ')
    .filter((term) => term.length >= 2);
}

function normalizeSearchCharacter(value: string) {
  return value
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, ' ');
}

function createNormalizedTextMap(value: string) {
  const entries: Array<{ character: string; start: number; end: number }> = [];
  let normalized = '';
  let index = 0;

  Array.from(value).forEach((character) => {
    const normalizedCharacter = normalizeSearchCharacter(character);
    Array.from(normalizedCharacter).forEach((mappedCharacter) => {
      normalized += mappedCharacter;
      entries.push({
        character: mappedCharacter,
        start: index,
        end: index + character.length,
      });
    });
    index += character.length;
  });

  return { normalized, entries };
}

export function getContentBankHighlightTerms(query: string) {
  return Array.from(new Set(tokenizeSearchText(query))).sort((first, second) => {
    return second.length - first.length || first.localeCompare(second);
  });
}

export function getContentBankHighlightRanges(text: string, query: string) {
  const terms = getContentBankHighlightTerms(query);
  if (!text || terms.length === 0) {
    return [];
  }

  const { normalized, entries } = createNormalizedTextMap(text);
  const ranges: Array<{ start: number; end: number }> = [];

  terms.forEach((term) => {
    let searchIndex = 0;
    while (searchIndex < normalized.length) {
      const matchIndex = normalized.indexOf(term, searchIndex);
      if (matchIndex === -1) {
        break;
      }

      const firstEntry = entries[matchIndex];
      const lastEntry = entries[matchIndex + term.length - 1];
      if (firstEntry && lastEntry) {
        ranges.push({ start: firstEntry.start, end: lastEntry.end });
      }

      searchIndex = matchIndex + Math.max(term.length, 1);
    }
  });

  return ranges
    .sort((first, second) => first.start - second.start || second.end - first.end)
    .reduce<Array<{ start: number; end: number }>>((merged, range) => {
      const previous = merged[merged.length - 1];
      if (!previous || range.start > previous.end) {
        merged.push({ ...range });
        return merged;
      }

      previous.end = Math.max(previous.end, range.end);
      return merged;
    }, []);
}

export function buildContentBankHighlightSnippet(text: string, query: string, maxLength = 220) {
  const source = text.replace(/\s+/g, ' ').trim();
  if (!source) {
    return '';
  }

  const ranges = getContentBankHighlightRanges(source, query);
  if (ranges.length === 0) {
    return '';
  }

  const firstRange = ranges[0];
  let start = Math.max(0, firstRange.start - 90);
  let end = Math.min(source.length, start + maxLength);

  if (end === source.length) {
    start = Math.max(0, end - maxLength);
  }

  while (start > 0 && source[start] !== ' ') {
    start -= 1;
  }

  while (end < source.length && source[end] !== ' ') {
    end += 1;
  }

  return `${start > 0 ? '...' : ''}${source.slice(start, end).trim()}${
    end < source.length ? '...' : ''
  }`;
}

function scoreField(field: string, term: string, exactWeight: number, partialWeight: number) {
  if (!field) {
    return 0;
  }

  return field.split(' ').includes(term) ? exactWeight : field.includes(term) ? partialWeight : 0;
}

function buildSnippet(text: string, query: string) {
  return buildContentBankHighlightSnippet(text, query, 180) || text.replace(/\s+/g, ' ').slice(0, 180);
}

export function searchPublishedContentBankDocuments(
  documents: ContentBankDocument[],
  query: string,
  limit = 8
) {
  const terms = Array.from(new Set(tokenizeSearchText(query)));

  if (terms.length === 0) {
    return [];
  }

  const phrase = normalizeSearchText(query);

  return documents
    .filter((document) => document.published?.status === 'published')
    .map((document) => {
      const published = document.published;
      const metadata = normalizeContentBankMetadata(published?.metadata);
      const titleText = normalizeSearchText(published?.title || document.title || '');
      const keywordText = normalizeSearchText(
        [...metadata.categories, ...metadata.tags, ...metadata.searchKeywords].join(' ')
      );
      const bodyText = normalizeSearchText(
        [metadata.summary, ...(metadata.likelyQuestions || []), published?.plainText || ''].join(
          ' '
        )
      );
      const score = terms.reduce(
        (result, term) =>
          result +
          scoreField(titleText, term, 32, 18) +
          scoreField(keywordText, term, 28, 16) +
          scoreField(bodyText, term, 9, 4),
        phrase && titleText.includes(phrase) ? 40 : 0
      );

      return {
        id: document.id,
        title: published?.title || document.title,
        summary: metadata.summary || (published?.plainText || '').slice(0, 160),
        snippet: buildSnippet(published?.plainText || metadata.summary || '', query),
        categories: metadata.categories,
        tags: metadata.tags,
        score,
        source: 'lexical' as const,
      };
    })
    .filter((result) => result.score > 0)
    .sort((first, second) => second.score - first.score || first.title.localeCompare(second.title))
    .slice(0, limit);
}
